/**
 * Demo v2 provenance surface — persistent, unmistakable Demo V2
 * identification across every Institutional Portal page when the
 * tenant is a Demo institution.
 *
 * Detection: the tenant identifier itself is authoritative. Every
 * Demo tenant carries a `DEMO-XXXXXXXXX` id (Luhn 9-digit tail).
 * Production tenants use bare 9-digit `INST_VLK_ID`s. The prefix
 * check is deterministic and works even when the SPA is served
 * from the wrong host by accident.
 */
import React from "react";
import { useAuth } from "@/context/AuthContext";

/** True iff the caller is signed in as a Demo evaluator. */
export function useIsDemoTenant() {
  const { user } = useAuth();
  const iid = user?.institution_id || "";
  return typeof iid === "string" && iid.startsWith("DEMO-");
}

/**
 * Full-width persistent ribbon at the very top of the app when the
 * caller is a Demo tenant. Non-dismissible by design — the whole
 * point is that the evaluator cannot mistake the Demo Portal for
 * the production Institutional Portal.
 */
export function DemoProvenanceRibbon() {
  const { user } = useAuth();
  const isDemo = useIsDemoTenant();
  if (!isDemo) return null;
  return (
    <div
      className="vlk-demo-ribbon"
      data-testid="demo-v2-ribbon"
      role="status"
    >
      <div className="vlk-demo-ribbon-inner">
        <span className="vlk-demo-ribbon-stripe" aria-hidden="true" />
        <span className="vlk-demo-ribbon-label">DEMO&nbsp;V2</span>
        <span className="vlk-demo-ribbon-meta">
          Isolated Demo v2 tenancy · Time-boxed · Not production
        </span>
        <span className="vlk-demo-ribbon-chip" data-testid="demo-v2-tenant-chip">
          {user?.institution_id}
        </span>
      </div>
    </div>
  );
}

/**
 * Inline provenance chip meant to be placed at the top of each
 * page's content region so Demo provenance travels with the
 * workflow — not only at the header.
 */
export function DemoProvenanceChip({ label = "DEMO V2 · Isolated Demo v2 tenancy" }) {
  const isDemo = useIsDemoTenant();
  if (!isDemo) return null;
  return (
    <div className="vlk-demo-page-chip" data-testid="demo-v2-page-chip">
      <span className="vlk-demo-page-chip-dot" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}
